/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { FaTimes, FaComments } from 'react-icons/fa';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../../../services/notificationService';
import { useUser } from '../../../context/userContext/UserContextProvider';

const NotificationDrawer = ({ isOpen, onClose, onCountUpdate, isNavSmall }) => {
  const { currentUser } = useUser();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    if (!currentUser?.id) return;
    try {
      setLoading(true);
      const data = await notificationService.getChatNotifications();
      const unreadSchools = Array.isArray(data)
        ? data.filter((school) => (school.totalUnreadCount || 0) > 0)
        : [];
      setSchools(unreadSchools);
      onCountUpdate(); // Refresh the count when we fetch
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, currentUser?.id]);

  // Prevent background scrolling when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      let date = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
      if (date.getTime() > Date.now() + 60000) {
        date = new Date(dateString);
      }
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (e) {
      return '';
    }
  };

  const handleSchoolClick = () => {
    navigate('/ongoing-chats');
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity"
          onClick={onClose}
        ></div>
      )}

      {/* Drawer */}
      <div
        className={`fixed top-[72px] bottom-[72px] ${isNavSmall ? 'left-24' : 'left-60'} z-40 w-96 bg-[#F3F4F6] shadow-2xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-[16px]' : '-translate-x-[calc(100%+15rem)]'
        } flex flex-col rounded-[12px] overflow-hidden`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-white rounded-tl-xl">
          <h2 className="text-xl font-bold text-gray-800">Unread Chats</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && schools.length === 0 ? (
            <div className="flex justify-center p-4">
              <span className="text-gray-500">Loading unread chats...</span>
            </div>
          ) : schools.length > 0 ? (
            schools.map((school) => {
              return (
                <div
                  key={school.schoolCode}
                  className="flex items-start gap-3 px-4 py-3 cursor-pointer bg-white rounded-lg shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors duration-150"
                  onClick={handleSchoolClick}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm bg-blue-50">
                      <FaComments className="text-blue-500" size={20} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {school.schoolName}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5 font-medium">
                      {school.totalUnreadCount} unread message{school.totalUnreadCount > 1 ? 's' : ''}
                    </p>
                    {school.latestMessageTime && (
                      <p className="text-[10px] text-gray-400 mt-1">
                        Active {getTimeAgo(school.latestMessageTime)}
                      </p>
                    )}
                  </div>

                  {/* Red Badge */}
                  <div className="flex-shrink-0 mt-2">
                    <span className="w-2.5 h-2.5 bg-[rgb(237,28,36)] rounded-full inline-block"></span>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center text-gray-500 mt-8">
              No unread messages
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationDrawer;
